import React, { useCallback, useMemo, useState } from "react";
import { Spin, Empty, Table, Badge, theme } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { Adapter } from "../../../types/adapter";
import { AdapterListProps, getAdapterColumns } from "./AdapterList";

const { useToken } = theme;

interface GroupedAdapterListProps
  extends Omit<AdapterListProps, "records" | "pagination"> {
  records: Adapter[];
}

interface TableRow extends Partial<Adapter> {
  key: string | number;
  isGroupHeader?: boolean;
  groupName?: string;
  groupCount?: number;
  groupStatus?: string;
}

const formatAdapterName = (status: string): string => {
  const name = status.replace(/^[^_]+_/, "");
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const GroupedAdapterList: React.FC<GroupedAdapterListProps> = ({
  records,
  loading,
  editable,
  actions,
  viewRecord,
  editRecord,
  copyRecord,
  removeRecord,
}) => {
  const { token } = useToken();

  // Collapsed by default: a status only shows its adapter rows once expanded.
  const [expandedStatuses, setExpandedStatuses] = useState<Set<string>>(
    new Set()
  );

  const toggleStatus = useCallback((status: string) => {
    setExpandedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const { groupedRows, columns } = useMemo(() => {
    // Group adapters by status
    const groups: Record<string, Adapter[]> = {};
    records.forEach((adapter) => {
      const status = adapter.status || "unknown";
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(adapter);
    });

    // Sort groups alphabetically
    const sortedGroups = Object.entries(groups).sort(([a], [b]) =>
      a.localeCompare(b)
    );

    // Build flat data source with group headers
    const rows: TableRow[] = [];
    sortedGroups.forEach(([status, adapters]) => {
      rows.push({
        key: `header-${status}`,
        isGroupHeader: true,
        groupName: formatAdapterName(status),
        groupCount: adapters.length,
        groupStatus: status,
      });
      adapters.forEach((adapter) => {
        rows.push({ ...adapter, key: adapter.id!, groupStatus: status });
      });
    });

    // Build columns
    let cols = getAdapterColumns(editable, actions, {
      viewRecord,
      editRecord,
      copyRecord,
      removeRecord,
    });
    cols = cols.filter((c) => c.dataIndex !== "status");

    return { groupedRows: rows, columns: cols };
  }, [
    records,
    editable,
    actions,
    viewRecord,
    editRecord,
    copyRecord,
    removeRecord,
  ]);

  // Headers always render; adapter rows only render for expanded statuses.
  const dataSource = useMemo(
    () =>
      groupedRows.filter(
        (row) => row.isGroupHeader || expandedStatuses.has(row.groupStatus!)
      ),
    [groupedRows, expandedStatuses]
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Spin />
      </div>
    );
  }

  if (records.length === 0) {
    return <Empty description="No adapters" />;
  }

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      size="small"
      components={{
        body: {
          row: ({ children, ...props }: any) => {
            const record = dataSource.find(
              (r) => r.key === props["data-row-key"]
            );
            if (record?.isGroupHeader) {
              const status = record.groupStatus!;
              const isExpanded = expandedStatuses.has(status);
              return (
                <tr {...props}>
                  <td
                    colSpan={columns.length}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleStatus(status)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleStatus(status);
                      }
                    }}
                    style={{
                      borderTop: `1px solid ${token.colorBorderSecondary}`,
                      padding: "12px 16px 8px",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        color: token.colorTextSecondary,
                        fontWeight: 500,
                      }}
                    >
                      {isExpanded ? <DownOutlined /> : <RightOutlined />}
                      {record.groupName}
                      <Badge
                        count={record.groupCount}
                        style={{ backgroundColor: token.colorTextQuaternary }}
                      />
                    </span>
                  </td>
                </tr>
              );
            }
            return <tr {...props}>{children}</tr>;
          },
        },
      }}
    />
  );
};
